import { useState } from 'react';
import { Highlight, themes } from 'prism-react-renderer';

type Language = 'typescript' | 'python' | 'go' | 'json' | 'bash' | 'javascript';

interface DocsPageProps {
  serviceKey?: string;
  convexSiteUrl?: string;
}

const PERMISSION_ROWS = [
  { label: 'Get Credits', service: true, child: true },
  { label: 'Deduct Credits', service: true, child: true },
  { label: 'Add Credits', service: true, child: false },
  { label: 'Set Credits', service: true, child: false },
  { label: 'Create Child Keys', service: true, child: false },
  { label: 'List Keys', service: true, child: false },
  { label: 'Manage Webhooks', service: true, child: false },
];

const WEBHOOK_EVENTS = [
  { event: 'service_key.rotated', trigger: 'Service key was rotated' },
  { event: 'child_key.created', trigger: 'A new child key was created' },
  { event: 'child_key.revoked', trigger: 'A child key was revoked' },
  { event: 'credits.low_threshold', trigger: 'Credits dropped below configured threshold' },
  { event: 'credits.exhausted', trigger: 'Credits reached zero' },
];

const ERROR_CODES = [
  { status: '200', code: 'OK', meaning: 'Request succeeded' },
  { status: '400', code: 'Bad Request', meaning: 'Invalid request body, missing required fields, or invalid parameter values' },
  { status: '401', code: 'Unauthorized', meaning: 'Missing or invalid API key' },
  { status: '402', code: 'Payment Required', meaning: 'Insufficient credits to complete the deduction' },
  { status: '403', code: 'Forbidden', meaning: 'Child key attempted a restricted operation (add or set credits)' },
  { status: '404', code: 'Not Found', meaning: 'User or resource not found' },
  { status: '429', code: 'Too Many Requests', meaning: 'Rate limit exceeded. Check rateLimitPerMinute on your key' },
  { status: '500', code: 'Internal Server Error', meaning: 'Server-side failure. Retry with backoff' },
];

const TOC_ITEMS = [
  { id: 'quick-start', label: 'Quick Start' },
  { id: 'authentication', label: 'Authentication & Security' },
  { id: 'api-reference', label: 'API Reference' },
  { id: 'webhooks', label: 'Webhook Integration' },
  { id: 'integration-examples', label: 'Bring Your Own Backend' },
  { id: 'error-codes', label: 'Error Codes' },
];

const PRISM_LANG_MAP: Record<Language, string> = {
  typescript: 'typescript',
  javascript: 'javascript',
  python: 'python',
  go: 'go',
  json: 'json',
  bash: 'plain',
};

const LANG_LABELS: Record<Language, string> = {
  typescript: 'TypeScript',
  javascript: 'JavaScript',
  python: 'Python',
  go: 'Go',
  json: 'JSON',
  bash: 'Shell',
};

function CodeBlock({ code, label, language = 'typescript', onCopy }: { code: string; label?: string; language?: Language; onCopy?: () => void }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    if (onCopy) onCopy();
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          {label && (
            <span className="text-neutral-400 text-xs font-mono uppercase tracking-widest">
              {label}
            </span>
          )}
          <span className="text-[10px] font-mono text-neutral-500 bg-neutral-800 px-1.5 py-0.5 rounded">
            {LANG_LABELS[language]}
          </span>
        </div>
        <button
          onClick={handleCopy}
          className="terminal-button px-2 py-1 text-xs font-mono"
        >
          {copied ? '[ OK ]' : '[ COPY ]'}
        </button>
      </div>
      <Highlight theme={themes.oneDark} code={code.trim()} language={PRISM_LANG_MAP[language]}>
        {({ style, tokens, getLineProps, getTokenProps }) => (
          <pre
            className="font-mono text-[13px] leading-relaxed p-4 rounded border border-neutral-700/60 overflow-x-auto"
            style={{ ...style, background: '#282c34', textShadow: 'none' }}
          >
            {tokens.map((line, i) => (
              <div key={i} {...getLineProps({ line })}>
                {line.map((token, key) => (
                  <span key={key} {...getTokenProps({ token })} style={{ ...getTokenProps({ token }).style, textShadow: 'none' }} />
                ))}
              </div>
            ))}
          </pre>
        )}
      </Highlight>
    </div>
  );
}

function Badge({ children, variant }: { children: React.ReactNode; variant: 'warning' | 'info' }) {
  const colors = variant === 'warning'
    ? 'bg-red-900/30 text-red-400 border-red-500/50'
    : 'bg-terminal-orange/10 text-terminal-orange border-terminal-orange-dim';
  return (
    <span className={`inline-block text-xs font-mono px-2 py-0.5 rounded border ${colors} ml-2`}>
      {children}
    </span>
  );
}

export function DocsPage({ serviceKey, convexSiteUrl }: DocsPageProps) {
  const [showKey, setShowKey] = useState(false);
  const [selectedTab, setSelectedTab] = useState<'nodejs' | 'python' | 'go' | 'curl'>('nodejs');

  const isAuthenticated = !!serviceKey;
  const maskedKey = serviceKey
    ? serviceKey.slice(0, 8) + '•'.repeat(Math.max(0, serviceKey.length - 12)) + serviceKey.slice(-4)
    : '';
  const apiKey = showKey && serviceKey ? serviceKey : (isAuthenticated ? maskedKey : 'sk_your_service_key');
  const apiUrl = convexSiteUrl || 'https://your-deployment.convex.site';

  const TABS: Array<{ id: 'nodejs' | 'python' | 'go' | 'curl'; label: string }> = [
    { id: 'nodejs', label: 'Node.js / TypeScript' },
    { id: 'python', label: 'Python' },
    { id: 'go', label: 'Go' },
    { id: 'curl', label: 'curl' },
  ];

  const nodejsExample = `import express from "express";

const app = express();
app.use(express.json());

const KP_BASE_URL = "${apiUrl}";
const KP_SERVICE_KEY = "${apiKey}";

interface KPResponse {
  success?: boolean;
  credits?: number;
  previousCredits?: number;
  newCredits?: number;
  operation?: string;
  amount?: number;
  timestamp?: string;
  reason?: string;
  userId?: string;
  balance?: number;
}

async function kpRequest(
  path: string,
  options: { method?: string; body?: Record<string, unknown>; apiKey?: string } = {}
): Promise<KPResponse> {
  const { method = "GET", body, apiKey = KP_SERVICE_KEY } = options;

  const res = await fetch(\`\${KP_BASE_URL}\${path}\`, {
    method,
    headers: {
      "x-api-key": apiKey,
      "Content-Type": "application/json",
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const data = await res.json();

  if (!res.ok) {
    const error = new Error(data.error || \`Key Palace API error: \${res.status}\`);
    (error as Error & { status: number }).status = res.status;
    throw error;
  }

  return data;
}

app.post("/api/users/signup", async (req, res) => {
  const { userId, email, plan } = req.body;

  try {
    const keyResult = await kpRequest("/api/keys/create", {
      method: "POST",
      body: {
        name: \`\${email} - \${plan}\`,
        environment: "production",
        rateLimitPerMinute: plan === "pro" ? 120 : 30,
        metadata: { userId, plan },
      },
    });

    const initialCredits = plan === "pro" ? 5000 : 500;
    await kpRequest("/api/credits/add", {
      method: "POST",
      body: {
        userId,
        amount: initialCredits,
        reason: \`Initial grant - \${plan} plan\`,
      },
    });

    res.json({
      success: true,
      credits: initialCredits,
      apiKey: keyResult.apiKey,
    });
  } catch (err) {
    const error = err as Error & { status?: number };
    res.status(error.status || 500).json({ error: error.message });
  }
});

app.get("/api/credits/balance", async (req, res) => {
  const userId = req.headers["x-user-id"] as string;

  try {
    const balance = await kpRequest(\`/api/credits/balance?userId=\${userId}\`);
    res.json(balance);
  } catch (err) {
    const error = err as Error & { status?: number };
    res.status(error.status || 500).json({ error: error.message });
  }
});

app.post("/api/generate", async (req, res) => {
  const userId = req.headers["x-user-id"] as string;
  const { prompt } = req.body;

  try {
    await kpRequest("/api/credits/deduct", {
      method: "POST",
      body: {
        userId,
        amount: 10,
        reason: "Image generation",
        transactionId: \`gen_\${Date.now()}_\${userId}\`,
      },
    });

    const result = await generateImage(prompt);
    res.json({ success: true, result });
  } catch (err) {
    const error = err as Error & { status?: number };

    if (error.status === 402) {
      res.status(402).json({
        error: "Insufficient credits",
        message: "Please purchase more credits to continue.",
      });
      return;
    }

    if (error.status === 403) {
      res.status(403).json({
        error: "Forbidden",
        message: "This operation requires a service key.",
      });
      return;
    }

    res.status(error.status || 500).json({ error: error.message });
  }
});

async function generateImage(prompt: string): Promise<string> {
  return \`Generated: \${prompt}\`;
}

app.listen(3000, () => {
  console.log("Server running on port 3000");
});`;

  const pythonExample = `import os
import httpx
from fastapi import FastAPI, HTTPException, Header
from pydantic import BaseModel

app = FastAPI()

KP_BASE_URL = "${apiUrl}"
KP_SERVICE_KEY = "${apiKey}"


async def kp_request(
    path: str,
    method: str = "GET",
    body: dict | None = None,
    api_key: str | None = None,
) -> dict:
    headers = {
        "x-api-key": api_key or KP_SERVICE_KEY,
        "Content-Type": "application/json",
    }

    async with httpx.AsyncClient() as client:
        response = await client.request(
            method,
            f"{KP_BASE_URL}{path}",
            headers=headers,
            json=body,
        )

    data = response.json()

    if not response.is_success:
        raise HTTPException(
            status_code=response.status_code,
            detail=data.get("error", f"Key Palace API error: {response.status_code}"),
        )

    return data


class SignupRequest(BaseModel):
    user_id: str
    email: str
    plan: str = "free"


class GenerateRequest(BaseModel):
    prompt: str


@app.post("/api/users/signup")
async def signup(req: SignupRequest):
    key_result = await kp_request(
        "/api/keys/create",
        method="POST",
        body={
            "name": f"{req.email} - {req.plan}",
            "environment": "production",
            "rateLimitPerMinute": 120 if req.plan == "pro" else 30,
            "metadata": {"userId": req.user_id, "plan": req.plan},
        },
    )

    initial_credits = 5000 if req.plan == "pro" else 500
    await kp_request(
        "/api/credits/add",
        method="POST",
        body={
            "userId": req.user_id,
            "amount": initial_credits,
            "reason": f"Initial grant - {req.plan} plan",
        },
    )

    return {
        "success": True,
        "credits": initial_credits,
        "api_key": key_result["apiKey"],
    }


@app.get("/api/credits/balance")
async def get_balance(x_user_id: str = Header(...)):
    return await kp_request(f"/api/credits/balance?userId={x_user_id}")


@app.post("/api/generate")
async def generate(req: GenerateRequest, x_user_id: str = Header(...)):
    try:
        await kp_request(
            "/api/credits/deduct",
            method="POST",
            body={
                "userId": x_user_id,
                "amount": 10,
                "reason": "Image generation",
            },
        )
    except HTTPException as e:
        if e.status_code == 402:
            raise HTTPException(
                status_code=402,
                detail="Insufficient credits. Please purchase more credits.",
            )
        raise

    result = f"Generated: {req.prompt}"
    return {"success": True, "result": result}`;

  const goExample = `package main

import (
	"bytes"
	"context"
	"encoding/json"
	"fmt"
	"io"
	"net/http"
	"os"
	"time"
)

var (
	kpBaseURL    = "${apiUrl}"
	kpServiceKey = "${apiKey}"
	httpClient   = &http.Client{Timeout: 10 * time.Second}
)

type KPError struct {
	StatusCode int
	Message    string
}

func (e *KPError) Error() string {
	return fmt.Sprintf("Key Palace API error %d: %s", e.StatusCode, e.Message)
}

func kpRequest(ctx context.Context, method, path string, body any) (map[string]any, error) {
	var reqBody io.Reader
	if body != nil {
		b, err := json.Marshal(body)
		if err != nil {
			return nil, fmt.Errorf("marshal request body: %w", err)
		}
		reqBody = bytes.NewReader(b)
	}

	req, err := http.NewRequestWithContext(ctx, method, kpBaseURL+path, reqBody)
	if err != nil {
		return nil, fmt.Errorf("create request: %w", err)
	}

	req.Header.Set("x-api-key", kpServiceKey)
	req.Header.Set("Content-Type", "application/json")

	resp, err := httpClient.Do(req)
	if err != nil {
		return nil, fmt.Errorf("execute request: %w", err)
	}
	defer resp.Body.Close()

	var result map[string]any
	if err := json.NewDecoder(resp.Body).Decode(&result); err != nil {
		return nil, fmt.Errorf("decode response: %w", err)
	}

	if resp.StatusCode >= 400 {
		msg := "unknown error"
		if e, ok := result["error"].(string); ok {
			msg = e
		}
		return nil, &KPError{StatusCode: resp.StatusCode, Message: msg}
	}

	return result, nil
}

func handleSignup(w http.ResponseWriter, r *http.Request) {
	var req struct {
		UserID string \`json:"userId"\`
		Email  string \`json:"email"\`
		Plan   string \`json:"plan"\`
	}
	if err := json.NewDecoder(r.Body).Decode(&req); err != nil {
		http.Error(w, \`{"error":"invalid request body"}\`, http.StatusBadRequest)
		return
	}

	rateLimit := 30
	initialCredits := 500
	if req.Plan == "pro" {
		rateLimit = 120
		initialCredits = 5000
	}

	keyResult, err := kpRequest(r.Context(), "POST", "/api/keys/create", map[string]any{
		"name":               fmt.Sprintf("%s - %s", req.Email, req.Plan),
		"environment":        "production",
		"rateLimitPerMinute": rateLimit,
		"metadata":           map[string]any{"userId": req.UserID, "plan": req.Plan},
	})
	if err != nil {
		writeError(w, err)
		return
	}

	_, err = kpRequest(r.Context(), "POST", "/api/credits/add", map[string]any{
		"userId": req.UserID,
		"amount": initialCredits,
		"reason": fmt.Sprintf("Initial grant - %s plan", req.Plan),
	})
	if err != nil {
		writeError(w, err)
		return
	}

	writeJSON(w, http.StatusOK, map[string]any{
		"success": true,
		"credits": initialCredits,
		"apiKey":  keyResult["apiKey"],
	})
}

func handleDeduct(w http.ResponseWriter, r *http.Request) {
	userID := r.Header.Get("X-User-Id")
	if userID == "" {
		http.Error(w, \`{"error":"missing X-User-Id header"}\`, http.StatusBadRequest)
		return
	}

	result, err := kpRequest(r.Context(), "POST", "/api/credits/deduct", map[string]any{
		"userId": userID,
		"amount": 10,
		"reason": "API call",
	})
	if err != nil {
		writeError(w, err)
		return
	}

	writeJSON(w, http.StatusOK, result)
}

func writeJSON(w http.ResponseWriter, status int, data any) {
	w.Header().Set("Content-Type", "application/json")
	w.WriteHeader(status)
	json.NewEncoder(w).Encode(data)
}

func writeError(w http.ResponseWriter, err error) {
	if kpErr, ok := err.(*KPError); ok {
		writeJSON(w, kpErr.StatusCode, map[string]string{"error": kpErr.Message})
		return
	}
	writeJSON(w, http.StatusInternalServerError, map[string]string{"error": err.Error()})
}

func main() {
	mux := http.NewServeMux()
	mux.HandleFunc("POST /api/users/signup", handleSignup)
	mux.HandleFunc("POST /api/deduct", handleDeduct)

	fmt.Println("Server running on :3000")
	if err := http.ListenAndServe(":3000", mux); err != nil {
		fmt.Fprintf(os.Stderr, "server error: %v\\n", err)
		os.Exit(1)
	}
}`;

  const curlExample = `export KP_URL="${apiUrl}"
export KP_KEY="${apiKey}"

# Get balance
curl -s "$KP_URL/api/credits/balance?userId=user_123" \\
  -H "x-api-key: $KP_KEY"

# Get balance (SDK format)
curl -s -X POST "$KP_URL/api/credits/get" \\
  -H "x-api-key: $KP_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"userId": "user_123"}'

# Deduct credits
curl -s -X POST "$KP_URL/api/credits/deduct" \\
  -H "x-api-key: $KP_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"userId": "user_123", "amount": 10, "reason": "API call"}'

# Add credits (service key only)
curl -s -X POST "$KP_URL/api/credits/add" \\
  -H "x-api-key: $KP_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"userId": "user_123", "amount": 500, "reason": "Credit purchase"}'

# Set credits (service key only)
curl -s -X POST "$KP_URL/api/credits/set" \\
  -H "x-api-key: $KP_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"userId": "user_123", "amount": 1000, "reason": "Monthly reset"}'

# Create child key
curl -s -X POST "$KP_URL/api/keys/create" \\
  -H "x-api-key: $KP_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "Production Key", "environment": "production", "rateLimitPerMinute": 60}'

# List all keys
curl -s "$KP_URL/api/keys/list" \\
  -H "x-api-key: $KP_KEY"

# Create webhook
curl -s -X POST "$KP_URL/api/webhooks" \\
  -H "x-api-key: $KP_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://your-server.com/webhooks/keypalace",
    "events": ["credits.low_threshold", "credits.exhausted"],
    "secret": "whsec_your_signing_secret"
  }'`;

  const tabExamples: Record<string, string> = {
    nodejs: nodejsExample,
    python: pythonExample,
    go: goExample,
    curl: curlExample,
  };

  const TAB_LANGUAGES: Record<string, Language> = {
    nodejs: 'typescript',
    python: 'python',
    go: 'go',
    curl: 'bash',
  };

  const webhookNodeExample = `import crypto from "node:crypto";

function verifyWebhookSignature(
  payload: string,
  signature: string,
  secret: string
): boolean {
  const expected = crypto
    .createHmac("sha256", secret)
    .update(payload)
    .digest("hex");

  return crypto.timingSafeEqual(
    Buffer.from(signature, "hex"),
    Buffer.from(expected, "hex")
  );
}

app.post("/webhooks/keypalace", express.raw({ type: "application/json" }), (req, res) => {
  const signature = req.headers["x-keypalace-signature"] as string;
  const payload = req.body.toString();

  if (!verifyWebhookSignature(payload, signature, process.env.WEBHOOK_SECRET!)) {
    res.status(401).json({ error: "Invalid signature" });
    return;
  }

  const event = JSON.parse(payload);

  switch (event.type) {
    case "credits.exhausted":
      break;
    case "credits.low_threshold":
      break;
    case "child_key.created":
      break;
  }

  res.status(200).json({ received: true });
});`;

  const webhookPythonExample = `import hmac
import hashlib

def verify_webhook_signature(payload: bytes, signature: str, secret: str) -> bool:
    expected = hmac.new(
        secret.encode(),
        payload,
        hashlib.sha256,
    ).hexdigest()
    return hmac.compare_digest(signature, expected)`;

  return (
    <div className="flex min-h-screen">
      <nav className="hidden lg:block w-60 flex-shrink-0 sticky top-0 h-screen overflow-y-auto border-r border-terminal-orange-dim/30 px-5 py-8">
        <div className="text-terminal-orange-bright text-xs font-mono uppercase tracking-widest mb-6">
          Documentation
        </div>
        <ul className="space-y-1">
          {TOC_ITEMS.map((item) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                className="text-terminal-orange-dim text-sm font-mono hover:text-terminal-orange-bright transition-colors block py-1.5 px-2 rounded hover:bg-terminal-orange/5"
              >
                {item.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <main className="flex-1 max-w-3xl mx-auto px-6 sm:px-10 py-12 space-y-16">
        <header className="space-y-6 pb-8 border-b border-terminal-orange-dim/20">
          <h1 className="text-terminal-orange-bright text-4xl sm:text-5xl font-mono tracking-wide leading-tight">
            Key Palace
          </h1>
          <p className="text-terminal-orange text-lg font-mono leading-relaxed max-w-2xl">
            Credit management for SaaS applications. Two-tier API keys, token bucket rate limiting, real-time webhooks — all via HTTP.
          </p>

          {isAuthenticated ? (
            <div className="terminal-box p-4 border-yellow-500/50 bg-yellow-900/10">
              <p className="text-yellow-400 text-sm font-mono">
                Code examples below use your live API credentials.
              </p>
              <div className="flex items-center gap-2 mt-2">
                <button
                  onClick={() => setShowKey(!showKey)}
                  className="terminal-button px-3 py-1.5 text-xs font-mono"
                >
                  {showKey ? '[ HIDE KEY ]' : '[ REVEAL KEY ]'}
                </button>
              </div>
            </div>
          ) : (
            <div className="terminal-box p-4 border-terminal-orange-dim">
              <p className="text-terminal-orange-dim text-sm font-mono">
                Sign in to see examples populated with your real API credentials.
              </p>
            </div>
          )}
        </header>

        <section id="quick-start" className="space-y-8">
          <h2 className="text-terminal-orange-bright text-2xl sm:text-3xl font-mono">
            Quick Start
          </h2>
          <div className="space-y-3">
            <p className="text-terminal-orange-dim text-base font-mono leading-relaxed">
              Base URL: <span className="text-terminal-orange">{apiUrl}</span>
            </p>
            <p className="text-terminal-orange-dim text-base font-mono leading-relaxed">
              All requests require an <code className="text-sky-400 bg-neutral-800/60 px-1 py-0.5 rounded text-[13px]">x-api-key</code> header. Responses are JSON. CORS is enabled for all origins.
            </p>
          </div>

          <CodeBlock
            label="Check balance"
            language="bash"
            code={`curl -s ${apiUrl}/api/credits/balance?userId=user_123 \\\n  -H "x-api-key: ${apiKey}"`}
          />
          <CodeBlock
            label="Create a child key"
            language="bash"
            code={`curl -s -X POST ${apiUrl}/api/keys/create \\\n  -H "x-api-key: ${apiKey}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"name": "User 456 Production Key", "environment": "production"}'`}
          />
          <CodeBlock
            label="Deduct credits"
            language="bash"
            code={`curl -s -X POST ${apiUrl}/api/credits/deduct \\\n  -H "x-api-key: ${apiKey}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"userId": "user_123", "amount": 10, "reason": "API call"}'`}
          />
        </section>

        <section id="authentication" className="space-y-10 pt-4 border-t border-terminal-orange-dim/20">
          <h2 className="text-terminal-orange-bright text-2xl sm:text-3xl font-mono">
            Authentication & Security
          </h2>

          <div className="space-y-5">
            <h3 className="text-terminal-orange text-xl font-mono">
              Two-Tier Key System
            </h3>
            <p className="text-terminal-orange-dim text-base font-mono leading-relaxed">
              Key Palace uses two key types for different actors:
            </p>
            <ul className="text-terminal-orange-dim text-base font-mono space-y-3 pl-4">
              <li>
                <span className="text-terminal-orange">Service Key</span> (<code className="text-sky-400 bg-neutral-800/60 px-1 py-0.5 rounded text-[13px]">sk_*</code>): Full administrative access. Created when you sign up. Used server-side only. Never expose to clients.
              </li>
              <li>
                <span className="text-terminal-orange">Child Key</span> (<code className="text-sky-400 bg-neutral-800/60 px-1 py-0.5 rounded text-[13px]">ck_pk_*</code>): Limited access for your end users. Can read balance and deduct credits. Cannot add or set credits.
              </li>
            </ul>

            <div className="border border-terminal-orange-dim rounded overflow-hidden">
              <table className="w-full font-mono text-sm">
                <thead>
                  <tr className="border-b border-terminal-orange-dim">
                    <th className="text-left text-terminal-orange-dim px-4 py-2 text-xs uppercase tracking-widest">Operation</th>
                    <th className="text-center text-terminal-orange px-4 py-2 text-xs uppercase tracking-widest">Service Key</th>
                    <th className="text-center text-terminal-orange px-4 py-2 text-xs uppercase tracking-widest">Child Key</th>
                  </tr>
                </thead>
                <tbody>
                  {PERMISSION_ROWS.map((row, i) => (
                    <tr key={row.label} className={i < PERMISSION_ROWS.length - 1 ? 'border-b border-terminal-orange-dim/30' : ''}>
                      <td className="text-terminal-orange-dim px-4 py-2">{row.label}</td>
                      <td className="text-center px-4 py-2">
                        <span className="text-emerald-400">&#10003;</span>
                      </td>
                      <td className="text-center px-4 py-2">
                        {row.child
                          ? <span className="text-emerald-400">&#10003;</span>
                          : <span className="text-red-400">&#10007;</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="space-y-5">
            <h3 className="text-terminal-orange text-xl font-mono">
              Key Storage Security
            </h3>
            <p className="text-terminal-orange-dim text-base font-mono leading-relaxed">
              Keys are stored using PBKDF2 hashing. Your service key is never stored in plaintext — Key Palace only stores the derived hash.
            </p>
            <ul className="text-terminal-orange-dim text-base font-mono space-y-2 pl-4">
              <li>100,000 iterations, SHA-256 digest</li>
              <li>16-byte random salt per key, stored as <code className="text-sky-400 bg-neutral-800/60 px-1 py-0.5 rounded text-[13px]">salt:hash</code> hex format</li>
              <li>Timing-safe comparison via double HMAC to prevent side-channel attacks</li>
              <li>Raw keys are shown once on creation and never stored or retrievable</li>
            </ul>
          </div>

          <div className="space-y-5">
            <h3 className="text-terminal-orange text-xl font-mono">
              Security Best Practices
            </h3>
            <div className="terminal-box p-4 space-y-3">
              <div className="text-terminal-orange-dim text-base font-mono leading-relaxed">
                <span className="text-terminal-orange">HTTPS only</span> — All API calls must use HTTPS. Key Palace rejects HTTP requests.
              </div>
              <div className="text-terminal-orange-dim text-base font-mono leading-relaxed">
                <span className="text-terminal-orange">Service key isolation</span> — Store in environment variables. Never in client code, frontend bundles, or git repos.
              </div>
              <div className="text-terminal-orange-dim text-base font-mono leading-relaxed">
                <span className="text-terminal-orange">Child key scoping</span> — Child keys can only deduct and read. Even if compromised, damage is bounded to spending existing credits.
              </div>
              <div className="text-terminal-orange-dim text-base font-mono leading-relaxed">
                <span className="text-terminal-orange">IP allowlisting</span> — Set <code className="text-sky-400 bg-neutral-800/60 px-1 py-0.5 rounded text-[13px]">allowedIps</code> on child keys to restrict usage to known servers.
              </div>
              <div className="text-terminal-orange-dim text-base font-mono leading-relaxed">
                <span className="text-terminal-orange">Origin allowlisting</span> — Set <code className="text-sky-400 bg-neutral-800/60 px-1 py-0.5 rounded text-[13px]">allowedOrigins</code> for browser-based child key usage.
              </div>
              <div className="text-terminal-orange-dim text-base font-mono leading-relaxed">
                <span className="text-terminal-orange">Key rotation</span> — Rotate service keys periodically via the dashboard. Old keys are invalidated immediately.
              </div>
              <div className="text-terminal-orange-dim text-base font-mono leading-relaxed">
                <span className="text-terminal-orange">Key expiry</span> — Set <code className="text-sky-400 bg-neutral-800/60 px-1 py-0.5 rounded text-[13px]">expiresAt</code> on child keys for time-limited access.
              </div>
              <div className="text-terminal-orange-dim text-base font-mono leading-relaxed">
                <span className="text-terminal-orange">Webhook verification</span> — Always verify HMAC-SHA256 signatures on incoming webhooks. Never trust unverified payloads.
              </div>
              <div className="text-terminal-orange-dim text-base font-mono leading-relaxed">
                <span className="text-terminal-orange">Rate limiting</span> — Every child key has a configurable <code className="text-sky-400 bg-neutral-800/60 px-1 py-0.5 rounded text-[13px]">rateLimitPerMinute</code>. Set it as low as your use case allows.
              </div>
              <div className="text-terminal-orange-dim text-base font-mono leading-relaxed">
                <span className="text-terminal-orange">Idempotency</span> — Use <code className="text-sky-400 bg-neutral-800/60 px-1 py-0.5 rounded text-[13px]">transactionId</code> on deduct/add/set calls to prevent duplicate charges. Key Palace deduplicates by transaction ID.
              </div>
              <div className="text-terminal-orange-dim text-base font-mono leading-relaxed">
                <span className="text-terminal-orange">Audit logging</span> — All credit operations are recorded in the transactions table with timestamps, amounts, and actor information.
              </div>
            </div>
          </div>

          <div className="space-y-5">
            <h3 className="text-terminal-orange text-xl font-mono">
              Rate Limiting
            </h3>
            <p className="text-terminal-orange-dim text-base font-mono leading-relaxed">
              Key Palace uses a token bucket algorithm for rate limiting:
            </p>
            <ul className="text-terminal-orange-dim text-base font-mono space-y-2 pl-4">
              <li>Each key gets <code className="text-sky-400 bg-neutral-800/60 px-1 py-0.5 rounded text-[13px]">rateLimitPerMinute</code> tokens</li>
              <li>Tokens refill at <code className="text-sky-400 bg-neutral-800/60 px-1 py-0.5 rounded text-[13px]">rateLimitPerMinute / 60</code> per second</li>
              <li>Returns HTTP 429 with <code className="text-sky-400 bg-neutral-800/60 px-1 py-0.5 rounded text-[13px]">Retry-After</code> header when exhausted</li>
            </ul>
          </div>
        </section>

        <section id="api-reference" className="space-y-8 pt-4 border-t border-terminal-orange-dim/20">
          <h2 className="text-terminal-orange-bright text-2xl sm:text-3xl font-mono">
            API Reference
          </h2>

          <h3 className="text-terminal-orange text-xl font-mono">Credits</h3>

          <details className="terminal-box group">
            <summary className="cursor-pointer p-4 font-mono text-sm text-terminal-orange hover:text-terminal-orange-bright transition-colors">
              <span className="text-emerald-400 font-bold mr-2">GET</span> /api/credits/balance
            </summary>
            <div className="px-4 pb-4 space-y-3 border-t border-terminal-orange-dim/30 pt-3">
              <div>
                <div className="text-terminal-orange-dim text-xs font-mono uppercase tracking-widest mb-1">Query Parameters</div>
                <p className="text-terminal-orange-dim text-sm font-mono">
                  <code className="text-sky-400 bg-neutral-800/60 px-1 py-0.5 rounded text-[13px]">userId</code> (string, required) — The user ID to check.
                </p>
              </div>
              <div>
                <div className="text-terminal-orange-dim text-xs font-mono uppercase tracking-widest mb-1">Headers</div>
                <p className="text-terminal-orange-dim text-sm font-mono">
                  <code className="text-sky-400 bg-neutral-800/60 px-1 py-0.5 rounded text-[13px]">x-api-key</code> — Service key or child key.
                </p>
              </div>
              <CodeBlock
                label="Response (200)"
                language="json"
                code={`{
  "balance": 950,
  "version": 12,
  "lastUpdated": "2026-08-13T10:30:00.000Z",
  "totalDeducted": 50,
  "totalAdded": 1000
}`}
              />
            </div>
          </details>

          <details className="terminal-box group">
            <summary className="cursor-pointer p-4 font-mono text-sm text-terminal-orange hover:text-terminal-orange-bright transition-colors">
              <span className="text-amber-400 font-bold mr-2">POST</span> /api/credits/get
            </summary>
            <div className="px-4 pb-4 space-y-3 border-t border-terminal-orange-dim/30 pt-3">
              <CodeBlock
                label="Request Body"
                language="json"
                code={`{
  "userId": "user_123"
}`}
              />
              <CodeBlock
                label="Response (200)"
                language="json"
                code={`{
  "userId": "user_123",
  "credits": 950,
  "timestamp": "2026-08-13T10:30:00.000Z"
}`}
              />
            </div>
          </details>

          <details className="terminal-box group">
            <summary className="cursor-pointer p-4 font-mono text-sm text-terminal-orange hover:text-terminal-orange-bright transition-colors">
              <span className="text-amber-400 font-bold mr-2">POST</span> /api/credits/deduct
            </summary>
            <div className="px-4 pb-4 space-y-3 border-t border-terminal-orange-dim/30 pt-3">
              <CodeBlock
                label="Request Body"
                language="json"
                code={`{
  "userId": "user_123",
  "amount": 10,
  "reason": "Image generation",
  "transactionId": "txn_abc123"
}`}
              />
              <div>
                <div className="text-terminal-orange-dim text-xs font-mono uppercase tracking-widest mb-1">Fields</div>
                <ul className="text-terminal-orange-dim text-sm font-mono space-y-1 pl-4">
                  <li><code className="text-sky-400 bg-neutral-800/60 px-1 py-0.5 rounded text-[13px]">userId</code> (string, optional) — Defaults to key owner if omitted.</li>
                  <li><code className="text-sky-400 bg-neutral-800/60 px-1 py-0.5 rounded text-[13px]">amount</code> (number, required) — Must be positive.</li>
                  <li><code className="text-sky-400 bg-neutral-800/60 px-1 py-0.5 rounded text-[13px]">reason</code> (string, required) — Description of the charge.</li>
                  <li><code className="text-sky-400 bg-neutral-800/60 px-1 py-0.5 rounded text-[13px]">transactionId</code> (string, optional) — Idempotency key to prevent duplicate deductions.</li>
                </ul>
              </div>
              <CodeBlock
                label="Response (200)"
                language="json"
                code={`{
  "success": true,
  "userId": "user_123",
  "previousCredits": 950,
  "newCredits": 940,
  "operation": "deduct",
  "amount": 10,
  "timestamp": "2026-08-13T10:31:00.000Z",
  "reason": "Image generation"
}`}
              />
              <div className="text-red-400 text-xs font-mono">
                Error (402): Insufficient credits.
              </div>
            </div>
          </details>

          <details className="terminal-box group">
            <summary className="cursor-pointer p-4 font-mono text-sm text-terminal-orange hover:text-terminal-orange-bright transition-colors">
              <span className="text-amber-400 font-bold mr-2">POST</span> /api/credits/add
              <Badge variant="warning">SERVICE KEY ONLY</Badge>
            </summary>
            <div className="px-4 pb-4 space-y-3 border-t border-terminal-orange-dim/30 pt-3">
              <CodeBlock
                label="Request Body"
                language="json"
                code={`{
  "userId": "user_123",
  "amount": 500,
  "reason": "Credit purchase - Pro plan",
  "transactionId": "stripe_pi_abc123"
}`}
              />
              <CodeBlock
                label="Response (200)"
                language="json"
                code={`{
  "success": true,
  "userId": "user_123",
  "previousCredits": 940,
  "newCredits": 1440,
  "operation": "add",
  "amount": 500,
  "timestamp": "2026-08-13T10:32:00.000Z",
  "reason": "Credit purchase - Pro plan"
}`}
              />
              <div className="text-red-400 text-xs font-mono">
                Error (403): Child keys receive &quot;Forbidden: Child keys cannot add credits. Use service key for credit management.&quot;
              </div>
            </div>
          </details>

          <details className="terminal-box group">
            <summary className="cursor-pointer p-4 font-mono text-sm text-terminal-orange hover:text-terminal-orange-bright transition-colors">
              <span className="text-amber-400 font-bold mr-2">POST</span> /api/credits/set
              <Badge variant="warning">SERVICE KEY ONLY</Badge>
            </summary>
            <div className="px-4 pb-4 space-y-3 border-t border-terminal-orange-dim/30 pt-3">
              <CodeBlock
                label="Request Body"
                language="json"
                code={`{
  "userId": "user_123",
  "amount": 1000,
  "reason": "Monthly credit reset",
  "transactionId": "reset_aug_2026"
}`}
              />
              <div className="text-red-400 text-xs font-mono">
                Error (403): Child keys receive &quot;Forbidden: Child keys cannot set credits. Use service key for credit management.&quot;
              </div>
            </div>
          </details>

          <h3 className="text-terminal-orange text-xl font-mono pt-6">API Keys</h3>

          <details className="terminal-box group">
            <summary className="cursor-pointer p-4 font-mono text-sm text-terminal-orange hover:text-terminal-orange-bright transition-colors">
              <span className="text-amber-400 font-bold mr-2">POST</span> /api/keys/create
              <Badge variant="warning">SERVICE KEY ONLY</Badge>
            </summary>
            <div className="px-4 pb-4 space-y-3 border-t border-terminal-orange-dim/30 pt-3">
              <CodeBlock
                label="Request Body"
                language="json"
                code={`{
  "name": "User 456 Production Key",
  "environment": "production",
  "rateLimitPerMinute": 60,
  "metadata": {
    "plan": "pro",
    "region": "us-east-1"
  }
}`}
              />
              <div>
                <div className="text-terminal-orange-dim text-xs font-mono uppercase tracking-widest mb-1">Fields</div>
                <ul className="text-terminal-orange-dim text-sm font-mono space-y-1 pl-4">
                  <li><code className="text-sky-400 bg-neutral-800/60 px-1 py-0.5 rounded text-[13px]">name</code> (string, required) — Human-readable label.</li>
                  <li><code className="text-sky-400 bg-neutral-800/60 px-1 py-0.5 rounded text-[13px]">environment</code> (string, required) — &quot;production&quot;.</li>
                  <li><code className="text-sky-400 bg-neutral-800/60 px-1 py-0.5 rounded text-[13px]">rateLimitPerMinute</code> (number, optional) — Rate limit for this key.</li>
                  <li><code className="text-sky-400 bg-neutral-800/60 px-1 py-0.5 rounded text-[13px]">metadata</code> (object, optional) — Arbitrary key-value pairs stored with the key.</li>
                </ul>
              </div>
              <CodeBlock
                label="Response (200)"
                language="json"
                code={`{
  "success": true,
  "apiKey": {
    "keyHash": "a1b2c3d4...",
    "name": "User 456 Production Key",
    "environment": "production",
    "rateLimitPerMinute": 60
  },
  "message": "Child key created successfully"
}`}
              />
              <p className="text-terminal-orange-dim text-xs font-mono">
                The raw API key string (<code className="text-sky-400 bg-neutral-800/60 px-1 py-0.5 rounded text-[13px]">ck_pk_*</code>) is returned in this response only. Store it immediately. Key Palace does not store the plaintext key and cannot retrieve it later.
              </p>
            </div>
          </details>

          <details className="terminal-box group">
            <summary className="cursor-pointer p-4 font-mono text-sm text-terminal-orange hover:text-terminal-orange-bright transition-colors">
              <span className="text-emerald-400 font-bold mr-2">GET</span> /api/keys/metadata
              <Badge variant="warning">SERVICE KEY ONLY</Badge>
            </summary>
            <div className="px-4 pb-4 space-y-3 border-t border-terminal-orange-dim/30 pt-3">
              <div>
                <div className="text-terminal-orange-dim text-xs font-mono uppercase tracking-widest mb-1">Query Parameters</div>
                <p className="text-terminal-orange-dim text-sm font-mono">
                  <code className="text-sky-400 bg-neutral-800/60 px-1 py-0.5 rounded text-[13px]">keyHash</code> (string, required) — The hash of the key to look up.
                </p>
              </div>
            </div>
          </details>

          <details className="terminal-box group">
            <summary className="cursor-pointer p-4 font-mono text-sm text-terminal-orange hover:text-terminal-orange-bright transition-colors">
              <span className="text-emerald-400 font-bold mr-2">GET</span> /api/keys/list
              <Badge variant="warning">SERVICE KEY ONLY</Badge>
            </summary>
            <div className="px-4 pb-4 space-y-3 border-t border-terminal-orange-dim/30 pt-3">
              <p className="text-terminal-orange-dim text-sm font-mono">
                List all keys associated with your account.
              </p>
            </div>
          </details>

          <h3 className="text-terminal-orange text-xl font-mono pt-6">Webhooks</h3>

          <details className="terminal-box group">
            <summary className="cursor-pointer p-4 font-mono text-sm text-terminal-orange hover:text-terminal-orange-bright transition-colors">
              <span className="text-amber-400 font-bold mr-2">POST</span> /api/webhooks
              <Badge variant="warning">SERVICE KEY ONLY</Badge>
            </summary>
            <div className="px-4 pb-4 space-y-3 border-t border-terminal-orange-dim/30 pt-3">
              <CodeBlock
                label="Request Body"
                language="json"
                code={`{
  "url": "https://your-server.com/webhooks/keypalace",
  "events": ["credits.low_threshold", "credits.exhausted"],
  "secret": "whsec_your_signing_secret"
}`}
              />
            </div>
          </details>

          <details className="terminal-box group">
            <summary className="cursor-pointer p-4 font-mono text-sm text-terminal-orange hover:text-terminal-orange-bright transition-colors">
              <span className="text-emerald-400 font-bold mr-2">GET</span> /api/webhooks
              <Badge variant="warning">SERVICE KEY ONLY</Badge>
            </summary>
            <div className="px-4 pb-4 space-y-3 border-t border-terminal-orange-dim/30 pt-3">
              <p className="text-terminal-orange-dim text-sm font-mono">
                List all webhook subscriptions.
              </p>
            </div>
          </details>

          <details className="terminal-box group">
            <summary className="cursor-pointer p-4 font-mono text-sm text-terminal-orange hover:text-terminal-orange-bright transition-colors">
              <span className="text-blue-400 font-bold mr-2">PUT</span> /api/webhooks/:id
              <Badge variant="warning">SERVICE KEY ONLY</Badge>
            </summary>
            <div className="px-4 pb-4 space-y-3 border-t border-terminal-orange-dim/30 pt-3">
              <p className="text-terminal-orange-dim text-sm font-mono">
                Update a webhook subscription.
              </p>
            </div>
          </details>

          <details className="terminal-box group">
            <summary className="cursor-pointer p-4 font-mono text-sm text-terminal-orange hover:text-terminal-orange-bright transition-colors">
              <span className="text-red-400 font-bold mr-2">DELETE</span> /api/webhooks/:id
              <Badge variant="warning">SERVICE KEY ONLY</Badge>
            </summary>
            <div className="px-4 pb-4 space-y-3 border-t border-terminal-orange-dim/30 pt-3">
              <p className="text-terminal-orange-dim text-sm font-mono">
                Delete a webhook subscription.
              </p>
            </div>
          </details>
        </section>

        <section id="webhooks" className="space-y-8 pt-4 border-t border-terminal-orange-dim/20">
          <h2 className="text-terminal-orange-bright text-2xl sm:text-3xl font-mono">
            Webhook Integration
          </h2>

          <div className="space-y-5">
            <h3 className="text-terminal-orange text-xl font-mono">
              Signature Verification
            </h3>
            <p className="text-terminal-orange-dim text-base font-mono leading-relaxed">
              Webhook payloads are signed with HMAC-SHA256. The signature is sent in the <code className="text-sky-400 bg-neutral-800/60 px-1 py-0.5 rounded text-[13px]">x-keypalace-signature</code> header. Always verify the signature before processing.
            </p>

            <CodeBlock label="Node.js" language="typescript" code={webhookNodeExample} />
            <CodeBlock label="Python" language="python" code={webhookPythonExample} />
          </div>

          <div className="space-y-5">
            <h3 className="text-terminal-orange text-xl font-mono">
              Supported Events
            </h3>
            <div className="border border-terminal-orange-dim rounded overflow-hidden">
              <table className="w-full font-mono text-sm">
                <thead>
                  <tr className="border-b border-terminal-orange-dim">
                    <th className="text-left text-terminal-orange px-4 py-3 text-xs uppercase tracking-widest">Event</th>
                    <th className="text-left text-terminal-orange px-4 py-3 text-xs uppercase tracking-widest">Trigger</th>
                  </tr>
                </thead>
                <tbody>
                  {WEBHOOK_EVENTS.map((row, i) => (
                    <tr key={row.event} className={i < WEBHOOK_EVENTS.length - 1 ? 'border-b border-terminal-orange-dim/30' : ''}>
                      <td className="text-sky-400 px-4 py-2.5">{row.event}</td>
                      <td className="text-terminal-orange-dim px-4 py-2.5">{row.trigger}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="space-y-5">
            <h3 className="text-terminal-orange text-xl font-mono">
              Retry Policy
            </h3>
            <p className="text-terminal-orange-dim text-base font-mono leading-relaxed">
              Failed webhook deliveries are retried with exponential backoff:
            </p>
            <ul className="text-terminal-orange-dim text-base font-mono space-y-2 pl-4">
              <li>Attempt 1: Immediate</li>
              <li>Attempt 2: After 1 minute</li>
              <li>Attempt 3: After 5 minutes</li>
              <li>Maximum 3 retries</li>
            </ul>
            <p className="text-terminal-orange-dim text-base font-mono leading-relaxed">
              A delivery is considered failed if your endpoint returns a non-2xx status code or does not respond within 10 seconds.
            </p>
          </div>
        </section>

        <section id="integration-examples" className="space-y-8 pt-4 border-t border-terminal-orange-dim/20">
          <div className="space-y-3">
            <h2 className="text-terminal-orange-bright text-2xl sm:text-3xl font-mono">
              Bring Your Own Backend
            </h2>
            <p className="text-terminal-orange-dim text-base font-mono leading-relaxed">
              Complete integration examples for Node.js, Python, Go, and curl. Each example shows user signup, balance checking, and credit deduction.
            </p>
          </div>

          <div className="flex gap-1 border-b border-terminal-orange-dim/30 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setSelectedTab(tab.id)}
                className={`px-5 py-2.5 font-mono text-sm whitespace-nowrap transition-colors ${
                  selectedTab === tab.id
                    ? 'text-terminal-orange-bright border-b-2 border-terminal-orange'
                    : 'text-terminal-orange-dim hover:text-terminal-orange'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <CodeBlock code={tabExamples[selectedTab]} language={TAB_LANGUAGES[selectedTab]} />
        </section>

        <section id="error-codes" className="space-y-8 pt-4 border-t border-terminal-orange-dim/20">
          <h2 className="text-terminal-orange-bright text-2xl sm:text-3xl font-mono">
            Error Codes
          </h2>

          <div className="border border-terminal-orange-dim rounded overflow-hidden overflow-x-auto">
            <table className="w-full font-mono text-sm">
              <thead>
                <tr className="border-b border-terminal-orange-dim">
                  <th className="text-left text-terminal-orange px-4 py-3 text-xs uppercase tracking-widest">Status</th>
                  <th className="text-left text-terminal-orange px-4 py-3 text-xs uppercase tracking-widest">Code</th>
                  <th className="text-left text-terminal-orange px-4 py-3 text-xs uppercase tracking-widest">Meaning</th>
                </tr>
              </thead>
              <tbody>
                {ERROR_CODES.map((row, i) => (
                  <tr key={row.status} className={i < ERROR_CODES.length - 1 ? 'border-b border-terminal-orange-dim/30' : ''}>
                    <td className="text-sky-400 px-4 py-2.5">{row.status}</td>
                    <td className="text-terminal-orange px-4 py-2.5">{row.code}</td>
                    <td className="text-terminal-orange-dim px-4 py-2.5">{row.meaning}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-terminal-orange-dim text-base font-mono leading-relaxed">
            All error responses follow this format:
          </p>
          <CodeBlock language="json" code={`{\n  "error": "Human-readable error message"\n}`} />
          <p className="text-terminal-orange-dim text-base font-mono leading-relaxed">
            For 402 errors, check the user&apos;s balance before retrying. For 429 errors, respect the rate limit window before sending more requests.
          </p>
        </section>

        <div className="border-t border-terminal-orange-dim/30 pt-8 pb-12 text-center">
          <p className="text-terminal-orange-dim text-xs font-mono">
            Key Palace Documentation
          </p>
        </div>
      </main>
    </div>
  );
}
